import { atom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { ClientError } from '~/lib/error/clientError'
import { UnknownTypeError } from '~/lib/error/unknownTypeError'
import type {
  YahooMemberRelationResponse,
  YahooMemberRelationVariables,
} from '~/routes/api.v2.yahoo.member.relation'
import { atomEffectWithError } from './error'
import { RedirectError } from './lib/redirectError'
import { reservationInputStateValue$ } from './stateMachine/context'
import { transition$ } from './stateMachine/state'

type YahooMemberRelation = YahooMemberRelationResponse & { status: number }

export const yahooMemberRelationQueryFn$ = atom<{ fn: () => Promise<YahooMemberRelation> }>({
  fn: () => callYahooMemberRelation(),
})

const yahooMemberRelation$ = atomWithQuery<YahooMemberRelation>((get) => {
  const state = get(reservationInputStateValue$)
  return {
    queryKey: ['yahooMemberRelation', state],
    queryFn: get(yahooMemberRelationQueryFn$).fn,
    gcTime: 0,
    staleTime: 0,
    enabled: state === 'YAHOO_MEMBER_LINKING',
    retry: false,
  }
})
yahooMemberRelation$.debugLabel = 'yahooMemberRelation$'

export const listenYahooMemberLinking$ = atomEffectWithError((get, set) => {
  const state = get(reservationInputStateValue$)
  if (state !== 'YAHOO_MEMBER_LINKING') {
    return
  }
  const yahooMemberRelation = get(yahooMemberRelation$)
  if (!yahooMemberRelation.isSuccess) {
    return
  }
  const data = yahooMemberRelation.data
  switch (data.type) {
    case 'relationCompleted':
    case 'relationSkipped':
      set.recurse(transition$, { type: 'yahooMemberRelatingCompleted' })
      break
    case 'reauthRequired':
      throw RedirectError.withUrl('YAHOO_REAUTH_YID', data.url)
    case 'relationError':
      throw ClientError.fromMemberRelation(data.error)
    case 'error':
      if (data.status === 400) {
        throw ClientError.fromString(data.errors[0].message)
      }
      throw new Error(data.errors[0].message)
    default:
      throw new UnknownTypeError(data)
  }
})

async function callYahooMemberRelation(): Promise<YahooMemberRelation> {
  // API 呼び出しの前に、session cookie をここで削除しておく
  await fetch('/api/v2/session', {
    method: 'DELETE',
    credentials: 'include',
  })

  const variables: YahooMemberRelationVariables = { redirectBackUrl: location.href }
  const result = await fetch('/api/v2/yahoo/member/relation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ variables }),
    credentials: 'include',
  })
  const response: YahooMemberRelationResponse = await result.json()
  return {
    ...response,
    status: result.status,
  }
}
